'use client';

import { HttpTypes } from "@medusajs/types"
import { useTenantAndVariant } from "@multitenancy/TenantAndVariantContext"
import Image from "next/image"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

interface SpotlightedProductsProps {
  products: HttpTypes.StoreProduct[]
  region: HttpTypes.StoreRegion
  title?: string
  subtitle?: string
}

export function SpotlightedProducts({
  products,
  title = "Featured Products",
  subtitle = "Discover Our Handpicked Selection"
}: SpotlightedProductsProps) {
  const { featureToggles } = useTenantAndVariant()

  const getAnimationClasses = () => {
    switch (featureToggles.productCardAnimation) {
      case 'fade':
        return 'group-hover:opacity-90'
      case 'zoom':
        return 'group-hover:scale-110 group-hover:brightness-105'
      case 'slide-up':
        return 'group-hover:-translate-y-2 group-hover:brightness-105'
      case 'glow':
        return 'group-hover:scale-105 group-hover:brightness-110 group-hover:saturate-110'
      default:
        return 'group-hover:scale-110 group-hover:brightness-105'
    }
  }

  const getCardContainerClasses = () => {
    const baseClasses = 'group-hover:shadow-xl transition-all duration-500'
    switch (featureToggles.productCardAnimation) {
      case 'glow':
        return `${baseClasses} group-hover:ring-2 group-hover:ring-primary`
      default:
        return baseClasses
    }
  }

  const getGridClasses = () => {
    if (featureToggles.gridColumns === 2) return 'grid-cols-2'
    if (featureToggles.gridColumns === 3) return 'grid-cols-2 md:grid-cols-3'
    if (featureToggles.gridColumns === 4) return 'grid-cols-2 md:grid-cols-4'
    return 'grid-cols-2 md:grid-cols-4' // default 4
  }

  // Display first 8 products
  const displayProducts = products.slice(0, 8)

  // Don't render if no products
  if (!displayProducts || displayProducts.length === 0) {
    return null
  }

  return (
    <section className="py-16 bg-white overflow-hidden">
      <div className="container overflow-hidden">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-light tracking-[0.15em] uppercase text-[#2B2C2D] mb-2">
            {title}
          </h2>
          {subtitle && (
            <p className="text-sm md:text-base text-gray-600 tracking-wider">
              {subtitle}
            </p>
          )}
        </div>

        <div className={`grid ${getGridClasses()} gap-6`}>
          {displayProducts.map((product) => {
            const thumbnail = product.thumbnail || product.images?.[0]?.url
            const price = product.variants?.[0]?.calculated_price

            return (
              <LocalizedClientLink
                key={product.id}
                href={`/products/${product.handle}`}
                className="group block cursor-pointer"
              >
                <div
                  className={`relative overflow-hidden mb-4 bg-gray-100 rounded-sm shadow-sm ${getCardContainerClasses()}`}
                  style={{ aspectRatio: '3/4' }}
                >
                  {thumbnail ? (
                    <Image
                      src={thumbnail}
                      alt={product.title || 'Product'}
                      fill
                      className={`object-cover transition-all duration-700 ${getAnimationClasses()}`}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">No image</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-500" />
                  {featureToggles.showProductReviews && (
                    <div className="absolute top-2 left-2 bg-white/90 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                      ⭐ 4.8
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-normal text-[#2B2C2D] mb-2 group-hover:underline decoration-1 underline-offset-4 text-center px-2 transition-all duration-300">
                  {product.title}
                </h3>
                {price && (
                  <p className="text-sm text-[#2B2C2D] font-normal text-center">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: price.currency_code?.toUpperCase() || 'USD'
                    }).format(price.calculated_amount || 0)}
                  </p>
                )}
              </LocalizedClientLink>
            )
          })}
        </div>
      </div>
    </section>
  )
}
